import { createContext, useContext, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { ThemedArea } from './ThemeProvider.js'
import { ThemeToggle } from './ThemeToggle.js'

/**
 * Shell top bar — ONE permanent bar at the top of the content column,
 * shared by every app (OS-window title bar). It lives on the ambient stage
 * (transparent) and its contents are filled by whichever app window is
 * ACTIVE, via a portal — so the bar element never remounts on app switch
 * and there's exactly one bar, not one per window.
 *
 * Flow:
 *  - <ShellTopBarProvider> wraps the shell, holding the slot DOM node.
 *  - <ShellTopBarSlot/> renders the permanent container once, in the layout.
 *  - Each app window renders <ShellTopBarPortal active={isActive}>{bar}</…>;
 *    only the active window's bar reaches the slot. Because every window's
 *    `active` derives from the same URL-computed state in one render pass,
 *    the switch is atomic — no two bars overlap.
 *  - <AppTopBar> is the shared bar row for simple apps (title + theme toggle
 *    + macOS-style red close). Rich apps (workspace) portal their own bar.
 */

interface ShellTopBarCtx {
  slot: HTMLElement | null
  setSlot: (el: HTMLElement | null) => void
}

const Ctx = createContext<ShellTopBarCtx>({ slot: null, setSlot: () => {} })

export function ShellTopBarProvider({ children }: { children: ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null)
  return <Ctx.Provider value={{ slot, setSlot }}>{children}</Ctx.Provider>
}

/** The permanent bar container — render once in the shell layout. Reserves
 *  the bar height even when empty so nothing below shifts on app switch. */
export function ShellTopBarSlot({ className = '' }: { className?: string }) {
  const { setSlot } = useContext(Ctx)
  return <div ref={setSlot} className={`ws-topbar-slot ${className}`} />
}

/** Portal an active window's bar content into the permanent slot. */
export function ShellTopBarPortal({ active, children }: { active: boolean; children: ReactNode }) {
  const { slot } = useContext(Ctx)
  if (!active || !slot) return null
  return createPortal(children, slot)
}

/** Shared bar row: optional left content (or a title), spacer, theme toggle,
 *  optional macOS-style red close. Themed to the app's area so its vars match
 *  the window it titles. */
export function AppTopBar({
  area = 'workspace',
  title,
  icon,
  left,
  right,
  onClose,
  closeTitle = 'Close',
}: {
  area?: 'drive' | 'workspace' | 'chat'
  title?: ReactNode
  /** Small icon rendered before the title (e.g. a mini-app's <MiniappIcon>).
   *  Ignored when `left` is provided (that fully replaces the title slot). */
  icon?: ReactNode
  left?: ReactNode
  /** Extra controls seated just before the theme toggle (e.g. chat's star toggle). */
  right?: ReactNode
  onClose?: () => void
  closeTitle?: string
}) {
  return (
    <ThemedArea area={area} className="ws-topbar ws-clear">
      {left ?? (title != null && (
        <span className="flex items-center gap-1.5 min-w-0">
          {icon != null && <span className="shrink-0 inline-flex items-center">{icon}</span>}
          <span className="text-xs font-medium themed-text truncate max-w-[280px] select-none cursor-default">
            {title}
          </span>
        </span>
      ))}
      <div className="flex-1" />
      {right && <div className="flex items-center mr-2">{right}</div>}
      <ThemeToggle area={area} />
      {onClose && (
        <button
          onClick={onClose}
          className="group/appclose ml-2 mr-1.5 w-3.5 h-3.5 rounded-full bg-[#ff5f57]/45 hover:bg-[#ff5f57] border border-black/10 flex items-center justify-center transition-colors"
          data-tooltip={closeTitle}
          aria-label={closeTitle}
        >
          <X size={9} strokeWidth={3} className="text-black/60 opacity-0 group-hover/appclose:opacity-100 transition-opacity pointer-events-none" />
        </button>
      )}
    </ThemedArea>
  )
}
